import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { EntityConfigDto, ReportsApiService } from './reports-api.service';

@Injectable({ providedIn: 'root' })
export class EntityConfigService {
  private readonly entityConfigSubject = new BehaviorSubject<EntityConfigDto | null>(null);
  readonly entityConfig$: Observable<EntityConfigDto | null> = this.entityConfigSubject.asObservable();

  constructor(
    private reportsApi: ReportsApiService,
    private authService: AuthService
  ) {
    // Refresh on login (currentUser$ becomes non-null) and on every change-entity (selectedDcr$).
    // Wait for any in-flight entity sync (e.g. page-load JWT resync) before calling the API,
    // so the Bearer token always matches the displayed entity.
    combineLatest([
      this.authService.currentUser$.pipe(map(user => user?.ID ?? null), distinctUntilChanged()),
      this.authService.selectedDcr$.pipe(distinctUntilChanged())
    ])
      .pipe(
        filter(([userId]) => !!userId),
        switchMap(() => this.authService.waitForEntitySync())
      )
      .subscribe(() => this.refresh());
  }

  refresh(): void {
    // The composite endpoint returns a single entity's config. In global data scope (global access
    // and no specific entity selected) the backend applies no entity filter and returns the first
    // row it finds — an arbitrary entity's config. Clear instead of showing someone else's numbers.
    if (this.authService.hasGlobalDataScope()) {
      this.entityConfigSubject.next(null);
      return;
    }
    this.reportsApi.getEntityConfig().subscribe({
      next: (config) => this.entityConfigSubject.next(config ?? null),
      error: (error) => {
        console.warn('[entity-config] failed to load entity config', error);
        this.entityConfigSubject.next(null);
      }
    });
  }

  get current(): EntityConfigDto | null {
    return this.entityConfigSubject.value;
  }

  get isCurrentEntityDataProvider(): boolean {
    const provider = this.entityConfigSubject.value?.ProviderConfig;
    return !!provider && provider.Status === 1 && (provider.RevenueShare ?? 0) > 0;
  }
}
